import { ChevronDown, Copy, RotateCcw } from "lucide-react";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { LogoMark } from "@/components/common/logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { isApiError } from "@/lib/api/errors";
import { isChunkLoadError } from "@/lib/chunk-reload";
import { reloadForNewVersion } from "@/lib/version";
import { cn } from "@/lib/utils";

interface Props {
  children: React.ReactNode;
  /** "page" keeps the surrounding shell alive; "app" takes the whole screen. */
  variant?: "app" | "page";
}

interface State {
  error: Error | null;
  info: string | null;
}

/**
 * Last line of defence: a render crash shouldn't leave a blank page — and it
 * shouldn't leave the person looking at it with nothing to report, either. The
 * message and the component stack are both on screen and copyable.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // No token or PII in here — the message and the component stack only.
    console.error("[rocketeer] render error", error, info.componentStack);
    this.setState({ info: info.componentStack ?? null });
  }

  private reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    // By the time a chunk error reaches the boundary, lazyWithReload has already
    // ruled out a stale deployment (it would have reloaded) and retried the
    // transient case. So this is a genuine load failure — usually the network —
    // and the honest action is retry, not a "new version" claim that isn't true.
    if (isChunkLoadError(error)) {
      return (
        <div
          className={cn(
            "grid place-items-center px-6",
            this.props.variant === "page" ? "py-16" : "min-h-dvh bg-background",
          )}
        >
          <div className="max-w-sm space-y-3 text-center">
            <LogoMark className="mx-auto size-9 text-brand" />
            <h1 className="text-lg font-semibold tracking-tight">Couldn't load this page</h1>
            <p className="text-sm text-muted-foreground">
              Part of the app didn't download — usually a dropped connection. Your data is safe.
            </p>
            <div className="flex justify-center gap-2 pt-1">
              <Button onClick={this.reset}>
                <RotateCcw className="size-4" />
                Try again
              </Button>
              <Button variant="outline" onClick={() => reloadForNewVersion()}>
                Reload
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const requestId = isApiError(error) ? error.requestId : null;
    const detail = [
      `${error.name}: ${error.message}`,
      requestId ? `Request-Id: ${requestId}` : null,
      window.location.pathname,
      info ? `\nComponent stack:${info}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const body = (
      <div className="w-full max-w-xl space-y-4">
        <div className="space-y-2">
          <LogoMark className="size-9 text-brand" />
          <h1 className="text-xl font-semibold tracking-tight">Something came loose</h1>
          <p className="text-sm text-muted-foreground">
            This part of the page hit an error it couldn't recover from. Nothing you'd entered
            elsewhere has been lost.
          </p>
        </div>

        {/* The message itself, not a shrug — it's what makes a bug report useful */}
        <Card className="overflow-hidden border-destructive/30">
          <p className="px-4 pt-3.5 pb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            What broke
          </p>
          <p className="px-4 pb-3.5 font-mono text-[13px] break-words text-destructive select-all">
            {error.name}: {error.message}
          </p>
          {requestId && (
            <p className="border-t px-4 py-2 font-mono text-[11px] text-muted-foreground select-all">
              Request-Id: {requestId}
            </p>
          )}
          {info && (
            <details className="border-t">
              <summary className="flex cursor-pointer items-center gap-1.5 px-4 py-2 text-xs text-muted-foreground hover:text-foreground">
                <ChevronDown className="size-3" />
                Component stack
              </summary>
              <pre className="max-h-52 overflow-auto border-t bg-muted/40 px-4 py-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
                {info.trim()}
              </pre>
            </details>
          )}
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button onClick={this.reset}>
            <RotateCcw className="size-4" />
            Try again
          </Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
          <Button
            variant="ghost"
            onClick={() => void navigator.clipboard?.writeText(detail).catch(() => undefined)}
          >
            <Copy className="size-4" />
            Copy details
          </Button>
        </div>
      </div>
    );

    if (this.props.variant === "page") {
      return <div className="flex justify-center py-10">{body}</div>;
    }

    return <div className="grid min-h-dvh place-items-center bg-background px-6">{body}</div>;
  }
}

/**
 * A boundary that forgets its error when the route changes — otherwise one
 * crashed page leaves the whole shell stuck on the error state.
 */
export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary key={pathname} variant="page">
      {children}
    </ErrorBoundary>
  );
}
